import type { components } from '@social-studio/api-client';
import { api } from '../../lib/api';
import { problemSlug, type Problem } from '../knowledge/problems';

export type Billing = components['schemas']['BillingDto'];
export type CreditTaskUsage = components['schemas']['CreditTaskUsageDto'];

export class BillingLoadError extends Error {
  constructor(readonly status: number) {
    super(`billing request failed with ${status}`);
  }

  /** The license platform could not be reached and no balance is known yet (`billing-unavailable`). */
  get unavailable(): boolean {
    return this.status === 503;
  }
}

/** `GET /v1/billing` (spec 10 §5, AC-10-056). */
export async function fetchBilling(): Promise<Billing> {
  const { data, response } = await api.GET('/v1/billing');
  if (!response.ok || !data) throw new BillingLoadError(response.status);
  return data;
}

/** "Guthaben anfragen" until credits can be bought in the app: a prepared e-mail to the operator. */
export function creditRequestHref(email: string, subject: string, body: string): string {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export type CreditPackage = components['schemas']['CreditPackageDto'];

/** `GET /v1/billing/packages` (spec 10 §5, S140-8): what an admin of a metered tenant can buy; empty otherwise. */
export async function fetchCreditPackages(): Promise<CreditPackage[]> {
  const { data, response } = await api.GET('/v1/billing/packages');
  if (!response.ok || !data) throw new BillingLoadError(response.status);
  return data.packages;
}

export type CreditCheckoutFailure = 'unavailable' | 'packageUnavailable' | 'failed';

/**
 * `POST /v1/billing/checkout` (S140-8): the Stripe Checkout page for one
 * package. `key` is the `Idempotency-Key`, so a repeated click opens the same
 * session instead of a second one.
 */
export async function startCreditCheckout(packageSlug: string, key: string): Promise<{ readonly url: string } | { readonly failure: CreditCheckoutFailure }> {
  try {
    const { data, error, response } = await api.POST('/v1/billing/checkout', { params: { header: { 'Idempotency-Key': key } }, body: { package: packageSlug } });
    if (response.ok && data) return { url: data.url };
    switch (problemSlug(error as Problem | undefined)) {
      case 'credit-purchase-unavailable':
        return { failure: 'unavailable' };
      case 'credit-package-unavailable':
        return { failure: 'packageUnavailable' };
      default:
        return { failure: 'failed' };
    }
  } catch {
    return { failure: 'failed' };
  }
}
